import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Flex } from "@chakra-ui/layout";
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/number-input";
import {
  Button,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/slider";
import { useFormikContext } from "formik";
import { at } from "lodash";
import React from "react";
import useFieldFast from "../../hooks/useFieldFast";
import useGetUserRole from "../../hooks/useGetUserRole";
import useInProgressDisable from "../../hooks/useInProgressDisable";
import useQuote from "../../hooks/useQuote";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import { FixAndFlipQuote } from "../../lib/models/short-term/short-term.model";
import ModalWrapper from "../ModalWrapper";
import FixAndFlipTierTable from "../stf-quote/FixAndFlipTierTable";

export type InputFieldProps = {
  label: string;
  altLabel: string;
  name: string;
  errorText?: string;
  helperText?: string;
};

function BorrowerTierSlider(props: InputFieldProps) {
  const { label, altLabel, name, helperText } = props;
  const [field, meta, helpers] = useFieldFast(props);
  const { values, setFieldValue } = useFormikContext<FixAndFlipForm>();
  const { colorMode } = useColorMode();
  const sliderThumbBg = useColorModeValue(lightMode[100], darkMode[200]);
  const { altTier, loanProduct } = values;
  const { value } = field;
  const { setValue } = helpers;
  const quoteContext = useQuote();
  const role = useGetUserRole();
  const { state } = quoteContext;
  const { metadata, quote } = state;
  const stfQuote = quote as FixAndFlipQuote;

  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value);
  const [min, setMin] = React.useState<number>(1);

  const handleChange = (value: any) => setInputValue(value);
  const handleChangeEnd = (value: any) => setFieldFastValue(value);

  const disableAltTier = useInProgressDisable("altTier");

  const isNewConstructionTier =
    loanProduct === "New Construction" && altTier === "No";

  const hasCalcTier =
    (stfQuote?.borrowerInfo?.buildsCompletedInThreeYears !== undefined &&
      stfQuote?.borrowerInfo?.buildsCompletedInThreeYears > 0) ||
    (stfQuote?.borrowerInfo?.fnfCompletedInThreeYears !== undefined &&
      stfQuote?.borrowerInfo?.fnfCompletedInThreeYears > 0) ||
    (stfQuote?.borrowerInfo?.completedExits !== undefined &&
      stfQuote?.borrowerInfo?.completedExits > 0);

  const setFieldFastValue = React.useCallback(
    (value) => {
      setValue(value.toString());
    },
    [setValue]
  );

  React.useEffect(() => {
    setValue(inputValue.toString());
  }, [inputValue, setValue]);

  React.useEffect(() => {
    if (metadata?.underwriterStatus === "Not Submitted") {
      if (loanProduct === "New Construction") {
        altTier === "Yes" ? setMin(4) : setMin(1);
        return;
      }
      setFieldValue("altTier", "No");
      setMin(1);
    }
  }, [
    loanProduct,
    setFieldValue,
    altTier,
    setMin,
    metadata?.underwriterStatus,
  ]);

  function renderHelperText() {
    const [touched, error] = at(meta, "touched", "error");

    if (touched && error) {
      return error;
    }
  }

  function handleAltToggle() {
    if (altTier === "Yes") {
      setFieldValue("altTier", "No");
    } else {
      setFieldValue("altTier", "Yes");
      setInputValue(4);
    }
  }

  return (
    <FormControl
      id={name}
      isDisabled={useInProgressDisable(name) || hasCalcTier}
    >
      <FormLabel>{isNewConstructionTier ? altLabel : label}</FormLabel>

      <InputGroup>
        <Flex width="full">
          <NumberInput
            type="number"
            defaultValue={1}
            step={1}
            max={5}
            min={min}
            maxW="100px"
            mr="2rem"
            value={inputValue}
            onChange={handleChange}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Slider
            flex="1"
            value={inputValue}
            focusThumbOnChange={false}
            isDisabled={useInProgressDisable(name) || hasCalcTier}
            onChange={(val: any) => {
              handleChange(val);
            }}
            onChangeEnd={(val: any) => {
              handleChangeEnd(val);
            }}
            defaultValue={1}
            step={1}
            max={5}
            min={min}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb
              border={colorMode === "dark" ? "1px solid" : "none"}
              borderColor={colorMode === "dark" ? "white" : ""}
              background={sliderThumbBg}
              fontSize="sm"
              w="50px"
              h="33px"
              borderRadius="md"
              children={inputValue}
            />
          </Slider>
        </Flex>
      </InputGroup>
      {meta.touched && meta.error ? (
        <FormErrorMessage>{renderHelperText()}</FormErrorMessage>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
      {(hasCalcTier || role === "External") && (
        <Button
          variant="link"
          marginTop={2}
          fontSize="xs"
          onClick={() => setIsOpen(!isOpen)}
          float="right"
        >
          What does this mean?
        </Button>
      )}

      {loanProduct === "New Construction" && !hasCalcTier && (
        <Button
          variant="link"
          marginTop={2}
          fontSize="xs"
          onClick={handleAltToggle}
          float="left"
          disabled={disableAltTier}
        >
          {altTier === "No"
            ? "Qualified FNF Borrower?"
            : "Use New Construction Tier?"}
        </Button>
      )}

      <ModalWrapper
        isOpen={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        title="Explanation of Borrower Tiers"
        size="lg"
        footer={<Button onClick={() => setIsOpen(!isOpen)}>Close</Button>}
      >
        <FixAndFlipTierTable
          constructionTable={isNewConstructionTier}
          quote={state.quote as FixAndFlipQuote}
        />
      </ModalWrapper>
    </FormControl>
  );
}

export default BorrowerTierSlider;
